import React, { memo, useEffect, useState } from 'react'
import { useCookies } from 'react-cookie'

import SaveService from 'services/save.service'

import { CardType, SaveStatus, defaultCard } from 'types/deck'
import { defaultCollectionV2Card } from 'redux/collectionV2/types'
import { CustomCard } from 'services/apiTypes/customCards.types'
import { SET_DECK_STATE } from 'redux/deck/actions/types'

import { store } from 'redux/store'
import { selectMenuCard, selectPreviousAndNextCard } from 'redux/deck/selectors'
import { setDeckStateAction } from 'redux/deck/actions'
import { setModalOverlay as setModalOverlayAction } from 'redux/active/actions'
import { useActionless, useActions, useAppSelector } from 'redux/hooks'

import CardDetailsOverlay, { NextPrevOptions } from 'components/elements/Overlay/CardDetailsOverlay'
import CardForm from 'components/card/cardForm/cardForm'
import Rulings from 'components/card/cardForm/Rulings'
import InDecks from 'components/collectionV2/cardDetails/InDecks'
import CardInfo from 'components/card/CardInfo/cardInfo'
import CardEditionsGridOverlay from 'components/card/CardEditionsGridOverlay'
import CustomCardEditForm from 'components/customCards/CustomCardEditForm'
import RelatedCards from 'components/collectionV2/cardDetails/RelatedCards'

import { convertCustomCardsToCardType } from 'hooks/customCard'

type CardDetailsTabTypes = 'options' | 'info' | 'rulings' | 'inDecks' | 'collectionRecords' | 'customCardDetails'

type Props = {
  open: boolean
  onClose: () => void
  card: CardType
  onCardUpdated: (card: CardType, removedCardIds?: number | number[]) => void
  saving?: SaveStatus
  nextPrevControls?: {
    previous: NextPrevOptions
    next: NextPrevOptions
  }
}

const CardModalStandalone = memo(({ open, card, onClose, onCardUpdated, saving, nextPrevControls }: Props) => {
  const [{ tbId: userId }] = useCookies(['tbId'])

  const [view, setView] = useState<CardDetailsTabTypes>('options')
  const [flipped, setFlipped] = useState(card?.flippedDefault || false)

  const [setDeckState] = useActionless(SET_DECK_STATE)

  const handleUpdateCustomCardInCardMap = (updatedCustomCard: CustomCard) => {
    // NOTE - keeping the existing card ID here, since converting generates a new ID. We wouldn't want to do that since we'll the card has only been edited, not replaced
    const updatedCard: CardType = {
      ...convertCustomCardsToCardType(updatedCustomCard)[0],
      id: card.id,
      qty: card.qty,
      customCmc: card.customCmc,
      modifier: card.modifier,
      colorLabel: card.colorLabel,
      categories: card.categories,
    }
    const { cardMap } = store.getState().deck

    // NOTE - we're updating the map directly here because we've updated the CARD data - NOT any deck data.
    // This will not cause a deck edit, cannot be undone, etc.
    setDeckState({ cardMap: { ...cardMap, [card.id]: updatedCard } })
  }

  useEffect(() => {
    // Change the tab to default if the card we're viewing doesn't support the current view
    if (!!card.customCard && ['collectionRecords', 'rulings'].includes(view)) setView('options')
    if (!card.customCard && ['customCardDetails'].includes(view)) setView('options')
  }, [card])

  return (
    <CardDetailsOverlay
      open={open}
      card={card || defaultCard}
      onClose={onClose}
      activeTabId={view}
      onTabChange={setView}
      onCardFlipped={setFlipped}
      onCardUpdated={onCardUpdated}
      lockFlippedAction
      saving={saving}
      nextPrevControls={nextPrevControls}
      tabs={[
        {
          label: 'Card options',
          id: 'options',
          body: (
            <CardForm
              card={card || defaultCard}
              onCardUpdated={onCardUpdated}
              flipped={flipped}
              nextPrevControls={nextPrevControls}
            />
          ),
        },
        {
          label: 'In decks',
          id: 'inDecks',
          disabled: !userId,
          hidden: !!card?.customCard && `${card?.customCard?.ownerId}` !== `${userId}`,
          body: <InDecks card={card || defaultCard} />,
        },
        {
          label: 'Collection records',
          id: 'collectionRecords',
          disabled: !userId,
          hidden: !!card.customCard,
          body: <RelatedCards collectionCard={{ ...defaultCollectionV2Card, card: card || defaultCard }} />,
        },
        {
          label: 'Custom card details',
          id: 'customCardDetails',
          hidden: !card?.customCard?.id || `${card?.customCard?.ownerId}` !== `${userId}`,
          body: <CustomCardEditForm card={card} onCardEdited={handleUpdateCustomCardInCardMap} />,
        },
        {
          label: 'Card info',
          id: 'info',
          body: <CardInfo card={card || defaultCard} flipped={flipped} />,
        },
        {
          label: 'Rulings',
          id: 'rulings',
          hidden: !!card?.customCard,
          body: <Rulings card={card || defaultCard} />,
        },
      ]}
    />
  )
})

export default CardModalStandalone

export const CardModal = () => {
  const menuCard = useAppSelector(selectMenuCard)
  const openMenuCardToEditions = useAppSelector(state => state.deck.openMenuCardToEditions)

  const [card, setCard] = useState(menuCard || defaultCard)
  const [saving, setSaving] = useState<SaveStatus>('saved')

  useEffect(() => {
    if (menuCard) return setCard(menuCard)

    setTimeout(() => setCard(defaultCard), 200)
  }, [menuCard])

  const [setDeckState, setModalOverlay] = useActions(setDeckStateAction, setModalOverlayAction)

  const [previous, next] = useAppSelector(state => selectPreviousAndNextCard(state, menuCard?.id || ''))

  const onClose = () => {
    setDeckState({ menuCardId: undefined, openMenuCardToEditions: false })
    setModalOverlay(null)
  }

  const onCardUpdated = (card: CardType) => {
    setSaving('saving')

    return SaveService.save(card)
      .then(() => {
        setSaving('saved')

        if (openMenuCardToEditions) onClose()
      })
      .catch(() => setSaving('error'))
  }

  return (
    <>
      <CardModalStandalone
        open={!!menuCard && !openMenuCardToEditions}
        saving={saving}
        card={card}
        onClose={onClose}
        onCardUpdated={onCardUpdated}
        nextPrevControls={{
          previous: {
            label: previous?.name,
            onClick: () => setDeckState({ menuCardId: previous?.id }),
            disabled: !previous,
          },
          next: {
            label: next?.name,
            onClick: () => setDeckState({ menuCardId: next?.id }),
            disabled: !next,
          },
        }}
      />

      <CardEditionsGridOverlay
        open={openMenuCardToEditions && !!menuCard}
        card={menuCard || defaultCard}
        onClose={onClose}
        onCardSelected={onCardUpdated}
      />
    </>
  )
}
